import React, { Fragment } from "react";
import "./unifiedtable.scss";

export type TableBlock =
  | {
      type: "image";
      src: string;
      alt: string;
      className?: string;
    }
  | {
      type: "lines";
      items: string[];
      className?: string;
    }
  | {
      type: "list";
      items: string[];
      ordered?: boolean;
      className?: string;
    }
  | {
      type: "paragraph";
      text: string;
      className?: string;
    }
  | {
      type: "heading";
      text: string;
      level?: 2 | 3 | 4 | 5 | 6;
      className?: string;
    };

export type TableCellContent = string | TableBlock | TableBlock[];

export interface TableCellDefinition {
  content?: TableCellContent;
  colSpan?: number;
  rowSpan?: number;
  className?: string;
  as?: "th" | "td";
}

export interface TableRowDefinition {
  cells: TableCellDefinition[];
  className?: string;
}

export interface TableDefinition {
  className?: string;
  head?: TableRowDefinition[];
  body: TableRowDefinition[];
  foot?: TableRowDefinition[];
}

interface UnifiedTableProps {
  definition: TableDefinition;
}

const renderBlock = (block: TableBlock, key: string) => {
  switch (block.type) {
    case "image":
      return (
        <img
          key={key}
          className={`unified-table__image ${block.className || ""}`.trim()}
          src={block.src}
          alt={block.alt}
        />
      );
    case "lines":
      return (
        <div key={key} className={`unified-table__lines ${block.className || ""}`.trim()}>
          {block.items.map((item, index) => (
            <Fragment key={`${key}-line-${index}`}>
              <span>{item}</span>
              {index < block.items.length - 1 && <br />}
            </Fragment>
          ))}
        </div>
      );
    case "list": {
      const ListTag = block.ordered ? "ol" : "ul";
      return (
        <ListTag key={key} className={`unified-table__list ${block.className || ""}`.trim()}>
          {block.items.map((item, index) => (
            <li key={`${key}-item-${index}`}>{item}</li>
          ))}
        </ListTag>
      );
    }
    case "heading": {
      const HeadingTag = `h${block.level || 4}` as keyof JSX.IntrinsicElements;
      return (
        <HeadingTag key={key} className={`unified-table__heading ${block.className || ""}`.trim()}>
          {block.text}
        </HeadingTag>
      );
    }
    case "paragraph":
      return (
        <p key={key} className={`unified-table__paragraph ${block.className || ""}`.trim()}>
          {block.text}
        </p>
      );
    default:
      return null;
  }
};

const renderContent = (content?: TableCellContent) => {
  if (content === undefined || content === null) {
    return null;
  }

  if (typeof content === "string") {
    return content;
  }

  if (Array.isArray(content)) {
    return content.map((block, index) => renderBlock(block, `block-${index}`));
  }

  return renderBlock(content, "block-0");
};

const UnifiedTable: React.FC<UnifiedTableProps> = ({ definition }) => {
  const tableClassName = ["unified-table", definition.className || "table"]
    .filter(Boolean)
    .join(" ");

  const renderRows = (rows: TableRowDefinition[], defaultCellTag: "th" | "td") =>
    rows.map((row, rowIndex) => (
      <tr key={`${defaultCellTag}-row-${rowIndex}`} className={row.className}>
        {row.cells.map((cell, cellIndex) => {
          const CellTag = cell.as || defaultCellTag;
          return (
            <CellTag
              key={`${defaultCellTag}-row-${rowIndex}-cell-${cellIndex}`}
              className={cell.className}
              colSpan={cell.colSpan}
              rowSpan={cell.rowSpan}
            >
              {renderContent(cell.content)}
            </CellTag>
          );
        })}
      </tr>
    ));

  return (
    <table className={tableClassName}>
      {definition.head && definition.head.length > 0 && (
        <thead>{renderRows(definition.head, "th")}</thead>
      )}
      <tbody>{renderRows(definition.body, "td")}</tbody>
      {definition.foot && definition.foot.length > 0 && (
        <tfoot>{renderRows(definition.foot, "td")}</tfoot>
      )}
    </table>
  );
};

export default UnifiedTable;
